import { Box, Flex, IconButton, Separator } from '@chakra-ui/react';

import isEqual from 'lodash/isEqual';

import { IconsLinear } from '@nocowanie/icons/linear';

import { SearchEngineDesktopProps } from './search-engine-desktop.props';

import { defaultSearchEngineTranslation, GuestsPickerInputDataModel } from '../../../models';
import { AutocompleteBaseItemModel } from '../../molecules/autocomplete/autocomplete-base-item.model';
import { GuestsPicker } from '../../molecules/guests-picker';
import { RangePicker, RangePickerDates } from '../../molecules/range-picker';
import { SearchLocation } from '../../molecules/search-location';

export const SearchEngineDesktop = <T extends AutocompleteBaseItemModel>({
    setValue,
    getValues,
    register,
    watch,
    isSubmitted,
    onInputValueUpdated,
    translationData = defaultSearchEngineTranslation,
    autocompleteData,
    isWebComponentVersion,
    ...props
}: SearchEngineDesktopProps<T>) => {
    const dividerStyles = {
        orientation: 'vertical',
        borderColor: 'inherit',
        opacity: 1,
        h: 'auto',
        w: 'auto',
        alignSelf: 'stretch',
        my: 2,
        mx: 0,
    } as const;

    const { isLoading } = autocompleteData;

    return (
        <Flex
            className="search-engine-container"
            borderWidth={'1px'}
            borderStyle={'solid'}
            borderColor={'gray.200'}
            borderRadius={'md'}
            {...props}
        >
            <SearchLocation
                autocompleteData={autocompleteData}
                setValue={setValue}
                w="215px"
                inputProps={{
                    inputProps: {
                        ...register('location'),
                        border: 'none',
                        _focus: {
                            boxShadow: 'none',
                        },
                        'data-testid': 'search-engine-location-input',
                    },
                }}
            />
            <Separator {...dividerStyles} />
            <Box w="190px">
                <RangePicker
                    selectedDates={[
                        getValues('dates.checkInDate'),
                        getValues('dates.checkOutDate'),
                    ]}
                    onRangeChange={(dates: RangePickerDates) => {
                        if (
                            dates[0] !== getValues('dates.checkInDate') ||
                            dates[1] !== getValues('dates.checkOutDate')
                        ) {
                            setValue('dates.checkInDate', dates[0]);
                            setValue('dates.checkOutDate', dates[1]);

                            onInputValueUpdated();
                        }
                    }}
                    inputProps={{
                        inputProps: {
                            border: 'none',
                            cursor: 'pointer',
                            px: 0,
                        },
                        'data-testid': 'search-engine-range-picker',
                    }}
                    isMobileBrowser={false}
                    isWebComponent={isWebComponentVersion}
                    flexDates={{
                        withFlexDates: true,
                        onFlexDateSelect: (flexDays: number) => {
                            setValue('flexDates', flexDays);
                            onInputValueUpdated();
                        },
                        selectedFlexDays: watch('flexDates'),
                        hideFlexDaysLabel: true,
                    }}
                />
            </Box>
            <Separator {...dividerStyles} />
            <GuestsPicker
                inputData={getValues('guests')}
                noArrow={true}
                onInputDataChanged={(data: GuestsPickerInputDataModel) => {
                    const newGuests = {
                        adultsCount: data.adultsCount || 1,
                        childrenCount: data.childrenCount || 0,
                        childrenAges: data.childrenAges
                            ? data.childrenAges.filter((age): age is number => age !== undefined)
                            : [],
                    };

                    if (!isEqual(newGuests, getValues('guests'))) {
                        setValue('guests', newGuests);
                        onInputValueUpdated();
                    }
                }}
                triggerProps={{
                    pr: 0,
                    pl: 2,
                    border: 0,
                    justifyContent: 'flex-start',
                    'data-testid': 'search-engine-guests-picker',
                }}
                isMobileBrowser={false}
            />
            <IconButton
                type="submit"
                aria-label={translationData.buttonSubmitAriaLabel}
                pl={1}
                pr={3}
                border={'none'}
                _hover={{
                    bg: 'none',
                }}
                data-testid="search-engine-submit-button"
                disabled={isLoading || isSubmitted}
            >
                <IconsLinear.Search size="md" />
            </IconButton>
        </Flex>
    );
};
